import { forwardRef, useEffect, useState } from "react";
import { ChangeHandler, FieldError } from "react-hook-form";
import {
  Field,
  FormFieldStatus,
  Hint,
  PasswordInput,
  Label,
} from "@gocardless/flux-react";
import { t, Trans } from "@lingui/macro";

import { TrackingErrorOrigin } from "../consts";

import { getInputStatus } from "src/utils/inputValidation";
import { FieldLevelError } from "src/common/api";
import { TrackingEvent } from "src/common/trackingEvents";
import { useSegment } from "src/technical-integrations/segment/useSegment";

interface PasswordComponentProps {
  error?: FieldError;
  apiError?: FieldLevelError;
  passwordValid?: boolean;
  minPassword: number;
  onChange?: ChangeHandler;
}

export const PasswordComponent = forwardRef<
  HTMLInputElement,
  PasswordComponentProps
>(
  (
    { error, apiError, passwordValid, minPassword, onChange, ...field },
    ref
  ) => {
    const { sendEvent } = useSegment();

    const [value, setValue] = useState("");
    const [focusTime, setFocusTime] = useState<number>();

    useEffect(() => {
      if (apiError?.message) {
        sendEvent(TrackingEvent.SIGNUP_PASSWORD_FIELD_VALIDATION_FAILED, {
          label: apiError.message,
          error_origin: TrackingErrorOrigin.server_side,
        });
      }
    }, [apiError?.message, sendEvent]);

    return (
      <Field>
        <Label htmlFor="password">
          <Trans id="Password">Password</Trans>{" "}
          <Trans id="min-var-characters">(min {minPassword} characters)</Trans>
        </Label>
        <PasswordInput
          {...field}
          id="password"
          ref={ref}
          onChange={(e) => {
            onChange?.(e);
            setValue(e.currentTarget.value);
          }}
          onFocus={() => setFocusTime(new Date().getTime())}
          onBlur={() => {
            const duration: number = focusTime
              ? new Date().getTime() - focusTime
              : 0;
            sendEvent(TrackingEvent.SIGNUP_FORM_FIELD_INTERACTION, {
              label: "Password",
              type: "Blur",
              time_spent: (duration / 1000).toFixed(2),
            });

            if (error?.type) {
              sendEvent(TrackingEvent.SIGNUP_PASSWORD_FIELD_VALIDATION_FAILED, {
                label: error?.type,
                error_origin: TrackingErrorOrigin.client_side,
              });
            }
          }}
          status={getInputStatus(!passwordValid, value)}
          showButtonLabel={t({
            id: "password-input.accessory-label.show",
            message: "Show password",
          })}
          hideButtonLabel={t({
            id: "password-input.accessory-label.hide",
            message: "Hide password",
          })}
        />
        {(error?.type === "minLength" ||
          error?.types?.type === "minLength") && (
          <Hint status={FormFieldStatus.Danger}>
            <Trans id="settings.user.password.password-too-short">
              Your password is too short
            </Trans>
          </Hint>
        )}
        {(error?.type === "passwordUserInfoPolicy" ||
          error?.types?.type === "passwordUserInfoPolicy") && (
          <Hint status={FormFieldStatus.Danger}>
            <Trans id="settings.user.password.password-policy-failed">
              Your password cannot contain user information such as first name,
              last name or email
            </Trans>
          </Hint>
        )}
        {(error?.type === "required" || error?.types?.type === "required") && (
          <Hint status={FormFieldStatus.Danger}>
            <Trans id="settings.user.password.field-required-warning">
              Password is required
            </Trans>
          </Hint>
        )}
        {apiError && (
          <Hint status={FormFieldStatus.Danger}>{apiError.message}</Hint>
        )}
      </Field>
    );
  }
);
